import { Injectable, Inject, PLATFORM_ID, effect } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { ILanguage, LanguageDirection } from '../_models/ILanguageDedinition';
import { environment } from '@app/../environments/environment';
import { MatwinsVariablesService } from './matwinsVariables.service';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {

  // accesseur pour les methodes statiques 
  public static instance: TranslationService | null = null;

  private currentLangSubject = new BehaviorSubject<string>('en');
  public currentLanguage$ = this.currentLangSubject.asObservable();
  private translationsSubject = new BehaviorSubject<any>({});
  public translations$ = this.translationsSubject.asObservable();
  private managedLanguagesSubject = new BehaviorSubject<ILanguage[] | undefined>([]);
  public managedLanguages$ = this.managedLanguagesSubject.asObservable();

  private between = TranslationService.instance?.instant('between');
  private and = TranslationService.instance?.instant('and');
  private characters = TranslationService.instance?.instant('characters');
  private at_least = TranslationService.instance?.instant('at least');
  private up_to = TranslationService.instance?.instant('up to');

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: any,
    private variables: MatwinsVariablesService
  ) {
    // 
    TranslationService.instance = this;


  }

  public get currentLanguageId(): number | undefined {
    const currentKey = this.currentLanguage;
    const lang = this.managedLanguagesSubject.value?.find(l => l.key === currentKey);
    return (lang as any)?.id;
  }

  private get defaultHeaders(): HttpHeaders {

    let headers = new HttpHeaders({
      'X-Custom-Matwins-BrowserId': this.variables.browserId || '',
      'X-Custom-Matwins-UseAuthentication': this.variables.useAuthentication.toString(),
      'X-Custom-Matwins-ClientId': this.variables.clientId || '',
      'X-Custom-Matwins-PosId': this.variables.posId?.toString() || ''
    });
    const accessToken = this.variables.getAccessToken();
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return headers;
  }

  public async initializeLanguage(preferredLang?: string): Promise<void> {
    try {
      await this.loadManagedLanguages();
      console.log(">>>>>>>> Managed languages ");
      const savedLang = this.getSavedLanguage();
      const browserLang = this.getBrowserLang();
      const defaultLang = preferredLang || savedLang || browserLang || 'en';
      await this.loadTranslations(defaultLang);
      console.log(">>>>>>>> Translations ");
    } catch (error) {
      console.warn('Failed to initialize language, using default:', error);
      this.currentLangSubject.next('en');
    }
  }

  public get BaseUrl(): string {
    return `${environment.apiBaseUrl}/api`;
  }

  public getClientSiteRootPath(): string {
    return "C:\\DefaultClientSite_Test";  // ICI //TODO: Changer ça
  }

  private async loadManagedLanguages(): Promise<void> {
    try {
      const url = `${this.BaseUrl}/translations/languages`;
      const languages = await this.http.get<ILanguage[]>(url, { headers: this.defaultHeaders }).toPromise();
      this.managedLanguagesSubject.next(languages);
    } catch (error) {
      console.warn('Failed to load managed languages, using fallback:', error);
      this.managedLanguagesSubject.next([
        {
          key: 'en',
          name: 'English',
          orientation: LanguageDirection.LeftToRight,
          logoPath: 'assets/flags/en.png'
        },
        {
          key: 'fr',
          name: 'Français',
          orientation: LanguageDirection.LeftToRight,
          logoPath: 'assets/flags/fr.png'
        }
      ]);
    }
  }

  private getSavedLanguage(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('selectedLanguage');
    }
    return null;
  }

  private getBrowserLang(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      const lang = navigator.language.split('-')[0];
      return lang.match(/en|fr/) ? lang : null;
    }
    return null;
  }

  private saveLanguage(language: string): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('selectedLanguage', language);
    }
  }

  public get currentLanguage(): string {
    return this.currentLangSubject.value;
  }

  public async use(language: string): Promise<void> {
    try {
      await this.loadTranslations(language);
    } catch (error) {
      console.error(`Failed to load language '${language}':`, error);
      throw error;
    }
  }

  private async loadTranslations(language: string): Promise<void> {
    try {
      let clientSiteRootPath = this.getClientSiteRootPath();
      let url = `${this.BaseUrl}/translations`;

      // Créer l'objet de paramètres
      const requestBody = {
        language: language,
        clientSiteRootPath: clientSiteRootPath
      };

      // Envoyer en POST avec le corps
      const translations = await this.http.post<any>(url, requestBody, { headers: this.defaultHeaders }).toPromise();
      this.translationsSubject.next(translations);
      this.currentLangSubject.next(language);
      this.saveLanguage(language);
    } catch (e) {
      console.error(`Failed to load translations for language '${language}':`, e);
    }
  }
  //##{propreties:[propertyKey1, property2, ...], intervel:[minValue:, maxValue]}##__messageKey

  public instant(key: string, params?: Record<string, string>): string {
    if (!key || key.trim().length === 0) {
      return key;
    }
    const keyCopy = key;
    key = key.split(' ').join('');

    let intervalMessage = '';

    if (params == undefined) {
      params = {}
    }

    if (key.indexOf('.multi-langual-messages.') && key.endsWith(`_${this.currentLanguage}`)) {
      key = `${key.trim()}<***`.replace(`_${this.currentLanguage}<***`, '');
    }

    if (key.startsWith('##') && key.indexOf('##__') > -1) {

      let properties: string[] = [];
      if (key.indexOf('propreties:[') > -1) {
        properties = key.split('propreties:[')[1].split(']')[0].split(',')

        properties.forEach((prop) => {
          const translatedProp = this.instant(prop);
          params[prop] = translatedProp ?? prop;
        });
      }

      if (key.indexOf('interval:[') > -1) {
        const between = this.instant('between');
        const and = this.instant('and');
        const characters = this.instant('characters');
        const at_least = this.instant('at least');
        const up_to = this.instant('up to');

        let intervalValues = key.split('interval:[')[1].split(']')[0].split(',');
        let minVal = (intervalValues[0] || '').trim();
        let maxVal = (intervalValues[1] || '').trim();

        if (minVal.length > 0 && maxVal.length > 0) {
          intervalMessage = `(${between} ${minVal} ${and} ${maxVal} ${characters}).`;
        } else if (minVal.length > 0) {
          intervalMessage = `(${at_least} ${minVal} ${characters}).`;
        } else if (maxVal) {
          intervalMessage = `(${up_to} ${maxVal} ${characters}).`;
        }
      }
      key = key.split('##__')[1];
    }

    const keys = key.split('.');
    let value = this.translationsSubject.value;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // First attempt failed, try normalized key with _Free-Translations
        let normalizedKey = key.split(' ').join(' ').toLowerCase().split(' ').join('-');
        let finalKey = `_Free-Translations.${normalizedKey}`;
        const freeKeys = finalKey.split('.');
        let freeValue = this.translationsSubject.value;

        for (const fk of freeKeys) {
          if (freeValue && typeof freeValue === 'object' && fk in freeValue) {
            freeValue = freeValue[fk];
          } else {
            return keyCopy;
          }
        }

        if (typeof freeValue === 'string' && params) {
          return this.replaceParams(freeValue, params);
        }
        return typeof freeValue === 'string' ? freeValue : keyCopy;
      }
    }

    if (typeof value === 'string' && params) {
      return `${this.replaceParams(value, params)} ${intervalMessage}`;
    }
    return typeof value === 'string' ? `${value}${intervalMessage}` : keyCopy;
  }

  private replaceParams(text: string, params: Record<string, string>): string {

    if (text.indexOf('Les propriétés suivantes ont été modifiées') > -1) {
      let yyy = 12;
    }

    let result = text;
    for (const [index, [key, value]] of Object.entries(params).entries()) {
      result = result.split(`{${index}}`).join(value);
      result = result.split(`{${key}}`).join(value);
    }

    return result;
  }
}
